import { CardReaderHandler } from '@lmt/unicorn-shared/app/components/card-reader/components/CardReaderHandler';
import { PingBackendComponent } from '@lmt/unicorn-shared/app/components/service/startup/login/PingBackendComponent';
import { TimeZoneChangeHandler } from '@lmt/unicorn-shared/app/components/service/TimeZoneChangeHandler';
import { isElectron } from '@lmt/unicorn-shared/app/helpers/common';
import { TabContents } from '@lmt/unicorn-shared/app/TabContents';
import { errorBoundaryFallbackFunction } from '@lmt/unicorn-shared/app/helpers/ErrorBoundary';
import * as Sentry from '@sentry/react';
import React, { Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import appStyles from './App.module.scss';
import { Loading } from './components/common/Loading';
import { GlobalForms } from './components/global-forms/GlobalForms';
import { NavPanel } from './components/navigation/NavPanel';
import { PingMergeAssistant } from './components/service/merge-assistant/PingMergeAssistant';
import { LoginLoader } from './components/service/startup/login/LoginLoader';
import { UniToolBar } from './components/toolbar/UniToolBar';
import { MiniScreenReadFileListener } from './components/miniscreen-importer/MiniScreenReadFileListener';
import { useClaims } from './features/auth/operations';
import { TabContextProvider } from './components/keep-alive/useTabs';

export const AuthenticatedApp = () => {
  const i18n = useTranslation();
  const claims = useClaims();
  const isCardReaderAvailable = useMemo(() => {
    return isElectron() || claims.permissions.mockEgkCardReader;
  }, [claims]);

  return (
    <LoginLoader>
      <TimeZoneChangeHandler>
        <QueryErrorResetBoundary>
          {({ reset }) => (
            <Sentry.ErrorBoundary
              fallback={errorBoundaryFallbackFunction}
              onReset={reset}
            >
              <TabContextProvider>
                <GlobalForms>
                  <PingBackendComponent />

                  {isCardReaderAvailable && <CardReaderHandler />}
                  <MiniScreenReadFileListener />
                  {claims.permissions.mergePatients && <PingMergeAssistant />}
                  <div className={appStyles.header}>
                    <UniToolBar />
                    <NavPanel />
                  </div>
                  <div className={appStyles.page}>
                    <QueryErrorResetBoundary>
                      {({ reset }) => (
                        <Sentry.ErrorBoundary
                          fallback={errorBoundaryFallbackFunction}
                          onReset={reset}
                        >
                          <Suspense
                            fallback={
                              <Loading
                                loading={true}
                                title={i18n.t('load.loading')}
                              />
                            }
                          >
                            <TabContents />
                          </Suspense>
                        </Sentry.ErrorBoundary>
                      )}
                    </QueryErrorResetBoundary>
                  </div>
                </GlobalForms>
              </TabContextProvider>
            </Sentry.ErrorBoundary>
          )}
        </QueryErrorResetBoundary>
      </TimeZoneChangeHandler>
    </LoginLoader>
  );
};
